import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../utils/apiClient';
import TopBar from '../../../layout/TopBar';
import HALSearchInput, { HALSearchQuery } from '../components/HALSearchInput';
import HALDetailsModal from '../components/HALDetailsModal';

interface HALMetadata {
    abstract?: string;
    keywords?: string[];
    authors?: string[];
    journal?: {
        title?: string;
        publisher?: string;
        issn?: string;
        volume?: string;
        issue?: string;
        pages?: string;
    };
    publication?: {
        type: string;
        subType?: string;
        date: string;
        language: string;
        domain: string[];
    };
    identifiers?: {
        doi?: string;
        isbn?: string;
        hal: {
            id: string;
            version: number;
            uri: string;
        };
    };
    files?: {
        main?: string;
        annexes?: string[];
        fulltext?: string;
    };
}

interface Ressource {
    _id: string;
    titre: string;
    source: {
        nom: string;
        identifiant: string;
        url: string;
    };
    dateImportation: string;
    halMetadata?: HALMetadata;
    generatedTags?: {
        principal: string[];
        secondary: string[];
        technical: string[];
        practical: string[];
    };
    classification?: {
        domain: string;
        subDomains: string[];
        nature: string;
    };
}

interface ApiResponse {
    ressources: Ressource[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

// Squelette de chargement inspiré de Jurisprudences
const HALGridSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-mcdm-third-300 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 animate-pulse flex flex-col h-full">
                {/* Header: Date & Type */}
                <div className="flex justify-between items-start mb-4">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                </div>

                {/* Title */}
                <div className="space-y-2 mb-6">
                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                </div>

                {/* Tags sections */}
                <div className="space-y-4 mb-auto">
                    {/* Principal Tags */}
                    <div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-32 mb-2"></div>
                        <div className="flex gap-2">
                            <div className="h-6 bg-blue-100 dark:bg-gray-700 rounded w-24"></div>
                            <div className="h-6 bg-blue-100 dark:bg-gray-700 rounded w-20"></div>
                        </div>
                    </div>
                    {/* Secondary Tags */}
                    <div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-28 mb-2"></div>
                        <div className="flex gap-2">
                            <div className="h-5 bg-green-50 dark:bg-gray-700 rounded w-20"></div>
                            <div className="h-5 bg-green-50 dark:bg-gray-700 rounded w-16"></div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-600 flex justify-between items-center">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-12"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                </div>
            </div>
        ))}
    </div>
);

const HALPage = () => {
    const [ressources, setRessources] = useState<Ressource[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [currentSearch, setCurrentSearch] = useState<HALSearchQuery>({ value: '', field: 'q' });
    const [selectedResource, setSelectedResource] = useState<Ressource | null>(null);

    const fetchRessources = useCallback(async (pageNum: number, search: HALSearchQuery) => {
        setLoading(true);
        try {
            let path = `/ressources?source=HAL&page=${pageNum}&limit=12`;

            if (search.value) {
                const val = encodeURIComponent(search.value);
                if (search.field === 'q') {
                    path += `&q=${val}`;
                } else {
                    path += `&${search.field}=${val}`;
                }
            }

            const response = await apiClient(path);

            if (!response.ok) {
                throw new Error('Erreur réseau');
            }

            const data: ApiResponse = await response.json();

            if (pageNum === 1) {
                setRessources(data.ressources);
            } else {
                setRessources(prev => [...prev, ...data.ressources]);
            }

            setHasMore(data.pagination.page < data.pagination.pages);
        } catch (error) {
            console.error("Erreur lors du chargement des ressources HAL:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRessources(1, currentSearch);
    }, [fetchRessources]);

    const handleSearch = (query: HALSearchQuery) => {
        setCurrentSearch(query);
        setPage(1);
        fetchRessources(1, query);
    };

    const clearFilter = () => {
        const empty: HALSearchQuery = { value: '', field: 'q' };
        handleSearch(empty);
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchRessources(nextPage, currentSearch);
        }
    };

    const handleResourceClick = (ressource: Ressource) => {
        setSelectedResource(ressource);
    };

    const closeDetailsModal = () => {
        setSelectedResource(null);
    };

    const hasGeneratedTags = (r: Ressource) =>
        r.generatedTags && (r.generatedTags.principal?.length > 0 || r.generatedTags.secondary?.length > 0);

    return (
        <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500 pb-10">
            <TopBar />
            <div className="pt-24 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-8">
                        <h1 className="text-2xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-6">
                            Publications scientifiques (HAL)
                        </h1>

                        {/* Search Bar */}
                        <HALSearchInput
                            onSearch={handleSearch}
                            initialValue={currentSearch.field === 'q' ? currentSearch.value : ''}
                        />

                        {/* Active Filter Badge */}
                        {currentSearch.value && currentSearch.field !== 'q' && (
                            <div className="mt-4 flex items-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-mcdm-secondary-100 text-mcdm-secondary-800 dark:bg-mcdm-secondary-900 dark:text-mcdm-secondary-200">
                                    {currentSearch.label || `${currentSearch.field}: ${currentSearch.value}`}
                                    <button
                                        type="button"
                                        onClick={clearFilter}
                                        className="flex-shrink-0 ml-2 h-4 w-4 rounded-full inline-flex items-center justify-center text-mcdm-secondary-500 hover:bg-mcdm-secondary-200 hover:text-mcdm-secondary-600 focus:outline-none focus:bg-mcdm-secondary-500 focus:text-white transition-colors"
                                    >
                                        <span className="sr-only">Supprimer le filtre</span>
                                        <svg className="h-3 w-3" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                            <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                                        </svg>
                                    </button>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* SKELETON LOADING STATE (Initial Load) */}
                    {loading && page === 1 && ressources.length === 0 ? (
                        <HALGridSkeleton />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {ressources.map((ressource) => (
                                <div
                                    key={ressource._id}
                                    onClick={() => handleResourceClick(ressource)}
                                    className="bg-white dark:bg-mcdm-third-300 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600 hover:shadow-md dark:hover:shadow-lg transition-all duration-200 flex flex-col h-full cursor-pointer hover:border-mcdm-secondary-300 dark:hover:border-mcdm-secondary-400 group"
                                >
                                    <div className="flex-grow">
                                        {/* Type & Date */}
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex flex-wrap gap-2">
                                                {ressource.halMetadata?.publication?.type && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                                        {ressource.halMetadata.publication.type}
                                                    </span>
                                                )}
                                                {ressource.generatedTags?.technical?.includes("Droit") && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                        Droit
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                                                {ressource.halMetadata?.publication?.date ? new Date(ressource.halMetadata.publication.date).toLocaleDateString() : ''}
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <h3 className="text-lg font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 mb-2 line-clamp-3 font-platypi group-hover:text-mcdm-secondary-500 dark:group-hover:text-mcdm-secondary-300 transition-colors" title={ressource.titre}>
                                            {ressource.titre}
                                        </h3>

                                        {/* Authors */}
                                        {ressource.halMetadata?.authors && ressource.halMetadata.authors.length > 0 && (
                                            <div className="mb-4">
                                                <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                                                    {ressource.halMetadata.authors.slice(0, 3).join(', ')}
                                                    {ressource.halMetadata.authors.length > 3 && ' et al.'}
                                                </p>
                                            </div>
                                        )}

                                        {/* GENERATED TAGS DISPLAY */}
                                        {hasGeneratedTags(ressource) ? (
                                            <div className="space-y-3 mb-4">
                                                {/* Principal Tags */}
                                                {ressource.generatedTags?.principal && ressource.generatedTags.principal.length > 0 && (
                                                    <div>
                                                        <h5 className="text-xs font-semibold text-mcdm-third-400 dark:text-mcdm-primary-200 mb-1.5 uppercase tracking-wide">
                                                            Sujets Principaux
                                                        </h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {ressource.generatedTags.principal.slice(0, 3).map((tag, idx) => (
                                                                <span key={`p-${idx}`} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Secondary Tags */}
                                                {ressource.generatedTags?.secondary && ressource.generatedTags.secondary.length > 0 && (
                                                    <div>
                                                        <h5 className="text-xs font-semibold text-mcdm-third-400 dark:text-mcdm-primary-200 mb-1.5 uppercase tracking-wide">
                                                            Sujets Secondaires
                                                        </h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {ressource.generatedTags.secondary.slice(0, 4).map((tag, idx) => (
                                                                <span key={`s-${idx}`} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* Fallback to Keywords if no generated tags */
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {ressource.halMetadata?.keywords?.slice(0, 3).map((keyword, idx) => (
                                                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                                        {keyword}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer / Actions */}
                                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-600 flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            {ressource.halMetadata?.files?.fulltext && (
                                                <span className="flex items-center text-xs text-green-600 dark:text-green-400 font-medium" title="Texte intégral disponible">
                                                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                    PDF
                                                </span>
                                            )}
                                        </div>
                                        <div
                                            className="text-sm font-medium text-mcdm-secondary-500 hover:text-mcdm-secondary-600 dark:text-mcdm-secondary-400 dark:hover:text-mcdm-secondary-300 transition-colors"
                                        >
                                            Voir les détails &rarr;
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && ressources.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 dark:text-gray-400 text-lg">
                                Aucune publication trouvée.
                            </p>
                        </div>
                    )}

                    {/* Load More Button */}
                    {hasMore && !loading && ressources.length > 0 && (
                        <div className="text-center py-8">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    loadMore();
                                }}
                                className="px-6 py-3 bg-white dark:bg-mcdm-third-300 border border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-100 rounded-xl hover:bg-gray-50 dark:hover:bg-mcdm-third-400 transition-colors duration-200 font-medium shadow-sm"
                            >
                                Charger plus
                            </button>
                        </div>
                    )}

                    {/* Load More Spinner (if loading next pages) */}
                    {loading && page > 1 && (
                        <div className="text-center py-12">
                            <div className="inline-flex items-center gap-2">
                                <div className="w-5 h-5 border-2 border-mcdm-secondary-400 border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-gray-500 dark:text-gray-400">Chargement...</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <HALDetailsModal
                isVisible={!!selectedResource}
                onClose={closeDetailsModal}
                resource={selectedResource}
            />
        </div>
    );
};

export default HALPage;
